import { When } from "react-if";
import { useMemo, useState } from "react";
import {
  SubscriptionStatus,
  UserTypes,
  accountingSystemProvider,
} from "shared";

import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";
import { env } from "../../../common/lib/env";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { Button } from "../../../common/Atoms/Button";
import { useUser } from "../../hooks/useUser";
import { Modal } from "../../../common/Atoms/Modal";
import { Heading } from "../../../common/Atoms/Typography/Heading";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useGetSubscriptionMetadata } from "../../hooks/useGetSubscription";
import { Notification } from "../../../common/Atoms/Notification";
import { RadioStack } from "../../../common/Atoms/RadioStack";
import { TextArea } from "../../../common/Atoms/TextArea";
import { useApiRequest } from "../../hooks/useApiRequest";

const disconnectReasons = [
  {
    value: `not_suitable`,
    title: `Not Suitable`,
    description: `Paidnice was not able to fulfill your requirements.`,
  },
  {
    value: `too_expensive`,
    title: `Too Expensive`,
    description: `Paidnice was too expensive.`,
  },
  {
    value: `not_enough_integrations`,
    title: `Not Enough Integrations`,
    description: `Paidnice did not integrate or work well with other software you use.`,
  },
  {
    value: `no_longer_needed`,
    title: `No Longer Needed`,
    description: `Paidnice is no longer needed.`,
  },
  {
    value: `too_complex`,
    title: `Too Complex`,
    description: `Paidnice was too complex to setup & use.`,
  },
  {
    value: `other`,
    title: `Other`,
    description: `Please help us improve by letting us know why you need to disconnect Paidnice.`,
  },
];

export function DisconnectButton() {
  const accountingSystemName = useAccountingSystemName();
  const currentOrg = useGetSelectedOrganisation();
  const { subscriptionMetadata } = useGetSubscriptionMetadata();
  const { data: user } = useUser();
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectReason, setDisconnectReason] = useState<string | null>(null);
  const [disconnectLoading, setDisconnectLoading] = useState<boolean>(false);
  const [otherText, setOtherText] = useState<string>(``);
  const apiRequest = useApiRequest();
  const connectSlug = accountingSystemProvider(currentOrg)?.connectSlug;

  const connectedUser = useMemo(() => {
    return (user?.orgs?.find(org => org.id === currentOrg.id)?.type === UserTypes.Inherit) || false;
  }, [user]);

  function handleDisconnectButtonClick() {
    setDisconnectModalOpen(true);
  }

  async function handleDisconnect() {
    try {
      setDisconnectLoading(true);

      await apiRequest({
        method: `POST`,
        url: `${env.apiUrl}/api/v1/organisation/disconnectReason`,
        data: {
          disconnectReason,
          disconnectReasonComments: otherText,
        },
      });
    }
    catch (err) {
      console.error(`Error saving disconnect reason`, err);

      // Swallow error and continue with disconnection
      return;
    }

    setDisconnectLoading(false);
    setDisconnectModalOpen(false);

    window.location.href = `${env.apiUrl}/connect/${connectSlug}/disconnect?org=${currentOrg.id}`;
  }

  if (!connectedUser || !connectSlug) {
    return null;
  }

  return (
    <div>
      <Modal
        open={ disconnectModalOpen }
        setOpen={ setDisconnectModalOpen }
        width={ `xl` }
      >
        <div>
          <Heading>
            { `Are you sure you want to disconnect ${accountingSystemName} from Paidnice?` }
          </Heading>
          <Paragraph
            className={ `mb-4` }
          >
            { `Paidnice will no longer get updates from ${accountingSystemName} and customers will loose access to the Paidnice portal.` }
          </Paragraph>

          <RadioStack
            selected={ disconnectReason || `` }
            onChange={ e => setDisconnectReason(e.value) }
            name={ `disconnect-reason` }
            options={ disconnectReasons }
          />

          <TextArea
            className={ `mt-4` }
            label={ `Additional Comments` }
            value={ otherText }
            onChange={ e => setOtherText(e.value?.toString()) }
            placeholder={ `Any additional comments on why you need to disconnect Paidnice. E.g what requirements it could not fulfill or missing integrations.` }
          />

          <When condition={ subscriptionMetadata?.status === SubscriptionStatus.ACTIVE }>
            <Notification
              type={ `warning` }
              className={ `mt-4` }
            >
              { `Disconnecting ${accountingSystemName} will not cancel your subscription. You can do that from the Billing page.` }
            </Notification>
          </When>

          <div className={ `flex w-full justify-center` }>
            <div className={ `space-x-2 mt-4` }>
              <Button
                onClick={ handleDisconnect }
                color={ `red` }
                loading={ disconnectLoading }
              >
                { `Confirm Disconnection` }
              </Button>
              <Button
                onClick={ () => setDisconnectModalOpen(false) }
                color={ `gray` }
              >
                { `Cancel` }
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <div className={ `mt-4` }>
        { /* Xero mandates its branded disconnect button */ }
        { currentOrg.type === `XERO` ? (
          <button
            className={ `cursor-pointer` }
            onClick={ handleDisconnectButtonClick }>
            <img src={ `/img/disconnect-white.svg` }
              alt={ `Disconnect ` + accountingSystemName }
              className={ `disconnect-button` }/>
          </button>
        ) : (
          <Button onClick={ handleDisconnectButtonClick }>{ `Disconnect ${accountingSystemName}` }</Button>
        ) }
      </div>
    </div>
  );
}
